import React, { useEffect, useState } from "react";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSelect,
    StandardModalState,
    StandardModalTable,
    StandardModalTableScroll,
    StandardModalTableSection,
    TextInputWithIcon,
    Toast,
    UserSelectionAvatar,
    UserSelectionCellText,
    UserSelectionCheckButton,
    UserSelectionChip,
    UserSelectionChipBody,
    UserSelectionChipIcon,
    UserSelectionChipText,
    UserSelectionControls,
    UserSelectionCount,
    UserSelectionFieldGroup,
    UserSelectionLabel,
    UserSelectionNameCell,
    UserSelectionRemoveButton,
    UserSelectionSearchInput,
    UserSelectionSelectButton,
    UserSelectionSummaryBar,
    UserSelectionSummaryLabel,
    UserSelectionSwitch,
    UserSelectionTable,
    UserSelectionToggleBox
} from "src/components";
import SearchIcon from "src/img/new/search-gray.svg";
import UsersIcon from "src/img/new/users.svg";
import CloseIcon from "src/img/CloseButton@2x.png";
import { USER_GROUP } from "src/constants/perks";
import getUsers from "../hooks/getUsers";
import { generateNameInitials } from "src/utils/helpers";
import BlueCheck from "src/img/survey/blue-check.svg";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";

const UsersModal = ({
    user,
    showUsersModal,
    handleCloseUsersModal,
    handleGiftPerkRecipients,
    userType,
    selectedRecipients = []
}) => {
    const [filterType, setFilterType] = useState(USER_GROUP.EMPLOYEES);
    const { users, isFetchingUsers } = getUsers(user.token, filterType);
    const [searchQuery, setSearchQuery] = useState("");
    const [allEmployeesInPerk, setAllEmployeesInPerk] = useState(false);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [allClientsInPerk, setAllClientsInPerk] = useState(false);
    const [selectedClients, setSelectedClients] = useState([]);

    // Set users table filter on edit
    useEffect(() => {
        if (userType) {
            setFilterType(userType);
        }
    }, [userType]);

    // Set selected users on editing gift perk users
    useEffect(() => {
        const clients = [];
        const employees = [];

        selectedRecipients.forEach(user => {
            if (user.isClient) {
                clients.push(user);
            } else {
                employees.push(user);
            }
        });

        setSelectedClients(clients);
        setSelectedEmployees(employees);
    }, [selectedRecipients]);

    // Pre-select `allEmployeesInPerk` & `allClientsInPerk`
    useEffect(() => {
        if (!isFetchingUsers && users.length > 0) {
            const totalEmployees = users.filter((user) => !user.isClient).length;
            const totalClients = users.filter((user) => user.isClient).length;

            setAllEmployeesInPerk(selectedEmployees.length === totalEmployees);
            setAllClientsInPerk(selectedClients.length === totalClients);
        }
    }, [isFetchingUsers, users, selectedEmployees, selectedClients]);

    const handleSearchChange = e => {
        setSearchQuery(e.target.value);
    };

    const handleFilterType = e => {
        setFilterType(e.target.value);
    };

    const handleSelectAll = () => {
        if (filterType === USER_GROUP.EMPLOYEES) {
            const isChecked = !allEmployeesInPerk;
            setAllEmployeesInPerk(isChecked);

            if (isChecked) {
                const employees = users.filter(user => user.isClient === false);
                setSelectedEmployees(employees);
            } else {
                setSelectedEmployees([]);
            }
        } else if (filterType === USER_GROUP.CLIENTS) {
            const isChecked = !allClientsInPerk;
            setAllClientsInPerk(isChecked);

            if (isChecked) {
                const clients = users.filter(user => user.isClient === true);
                setSelectedClients(clients);
            } else {
                setSelectedClients([]);
            }
        }
    };

    const handleSelectUser = user => {
        if (filterType === USER_GROUP.EMPLOYEES) {
            setSelectedEmployees(prevSelectedEmployees => {
                const updatedEmployees = prevSelectedEmployees.some(selectedUser => selectedUser.employeeUuid === user.employeeUuid)
                    ? prevSelectedEmployees.filter(selectedUser => selectedUser.employeeUuid !== user.employeeUuid)
                    : [...prevSelectedEmployees, user];

                const employeesCount = users.filter(user => !user.isClient).length;
                setAllEmployeesInPerk(updatedEmployees.length === employeesCount);

                return updatedEmployees;
            });
        } else if (filterType === USER_GROUP.CLIENTS) {
            setSelectedClients(prevSelectedClients => {
                const updatedClients = prevSelectedClients.some(selectedUser => selectedUser.employeeUuid === user.employeeUuid)
                    ? prevSelectedClients.filter(selectedUser => selectedUser.employeeUuid !== user.employeeUuid)
                    : [...prevSelectedClients, user];

                const clientsCount = users.filter(user => user.isClient).length;
                setAllClientsInPerk(updatedClients.length === clientsCount);

                return updatedClients;
            });
        }
    };

    const handleSaveRecipients = () => {
        const allSelectedUsers = [...selectedEmployees, ...selectedClients];
        if (!allSelectedUsers.length) {
            Toast.error("Please select some users.");
            return;
        }
        handleGiftPerkRecipients(allSelectedUsers);
        handleCloseUsersModal();
    };

    const clearSelectedEmployees = () => {
        setSelectedEmployees([]);
        setAllEmployeesInPerk(false);
    };

    const clearSelectedClients = () => {
        setSelectedClients([]);
        setAllClientsInPerk(false);
    };

    const isUserSelected = (user) => {
        return filterType === USER_GROUP.EMPLOYEES ?
            selectedEmployees.some(selectedUser => selectedUser.employeeUuid === user.employeeUuid) :
            selectedClients.some(selectedUser => selectedUser.employeeUuid === user.employeeUuid);
    };

    const filteredUsers = users.filter(user => {
        const { firstName, lastName } = user;
        const searchTerms = `${firstName.toLowerCase()} ${lastName.toLowerCase()}`;
        const isMatchingSearch = searchTerms.includes(searchQuery.toLowerCase());

        if (!isMatchingSearch) {
            return false;
        }

        return filterType === USER_GROUP.EMPLOYEES ? !user.isClient : user.isClient;
    });

    const selectedCount = filterType === USER_GROUP.EMPLOYEES ? selectedEmployees.length : selectedClients.length;

    const selectedUserGroupLabel = filterType === USER_GROUP.EMPLOYEES
        ? capitalizeString(pluralize(customNames.employees))
        : capitalizeString(pluralize(customNames.clients));
    const selectedUserGroupSearchLabel = filterType === USER_GROUP.EMPLOYEES
        ? pluralize(customNames.employees.toLowerCase())
        : pluralize(customNames.clients.toLowerCase());

    return (
        <StandardModal
            open={showUsersModal}
            onClose={handleCloseUsersModal}
            widthMode="table"
            title="Select Gift Perk Users"
            description="Select all users or choose specific people who can receive this gift perk."
            headerAside={(
                <div style={{ width: "13rem" }}>
                    <StandardModalSelect value={filterType} onChange={handleFilterType} disabled={isFetchingUsers}>
                        <option value={USER_GROUP.EMPLOYEES}>{capitalizeString(pluralize(customNames.employees))}</option>
                        <option value={USER_GROUP.CLIENTS}>{capitalizeString(pluralize(customNames.clients))}</option>
                    </StandardModalSelect>
                </div>
            )}
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton type="button" onClick={handleCloseUsersModal}>
                        Cancel
                    </StandardModalActionButton>
                    <StandardModalActionButton
                        type="button"
                        $variant="primary"
                        onClick={handleSaveRecipients}
                        disabled={isFetchingUsers}
                    >
                        Save
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <UserSelectionSummaryBar>
                    <UserSelectionSummaryLabel>Selected People:</UserSelectionSummaryLabel>
                    {!isFetchingUsers && selectedEmployees.length > 0 && (
                        <UserSelectionChip>
                            <UserSelectionChipBody>
                                <UserSelectionChipIcon>
                                    <img src={UsersIcon} alt="" />
                                </UserSelectionChipIcon>
                                <UserSelectionChipText>
                                    {allEmployeesInPerk ? "All" : selectedEmployees.length} {selectedEmployees.length === 1 ? capitalizeString(pluralize.singular(customNames.employees)) : capitalizeString(pluralize(customNames.employees))}
                                </UserSelectionChipText>
                            </UserSelectionChipBody>
                            <UserSelectionRemoveButton type="button" onClick={clearSelectedEmployees} aria-label="Remove selected employees">
                                <img src={CloseIcon} alt="" />
                            </UserSelectionRemoveButton>
                        </UserSelectionChip>
                    )}
                    {!isFetchingUsers && selectedClients.length > 0 && (
                        <UserSelectionChip>
                            <UserSelectionChipBody>
                                <UserSelectionChipIcon>
                                    <img src={UsersIcon} alt="" />
                                </UserSelectionChipIcon>
                                <UserSelectionChipText>
                                    {allClientsInPerk ? "All" : selectedClients.length} {selectedClients.length === 1 ? capitalizeString(pluralize.singular(customNames.clients)) : capitalizeString(pluralize(customNames.clients))}
                                </UserSelectionChipText>
                            </UserSelectionChipBody>
                            <UserSelectionRemoveButton type="button" onClick={clearSelectedClients} aria-label="Remove selected clients">
                                <img src={CloseIcon} alt="" />
                            </UserSelectionRemoveButton>
                        </UserSelectionChip>
                    )}
                </UserSelectionSummaryBar>
            </StandardModalSection>

            <StandardModalSection>
                <UserSelectionControls>
                    <UserSelectionFieldGroup>
                        <UserSelectionLabel>Select {selectedUserGroupLabel}</UserSelectionLabel>
                        <TextInputWithIcon
                            iconSrc={SearchIcon}
                            iconPosition="right"
                            iconOffset="0.875rem"
                            iconWidth="24px"
                            iconHeight="24px"
                        >
                            <UserSelectionSearchInput
                                placeholder={`Search ${selectedUserGroupSearchLabel}`}
                                type="text"
                                onChange={handleSearchChange}
                                disabled={isFetchingUsers}
                            />
                        </TextInputWithIcon>
                    </UserSelectionFieldGroup>
                    <UserSelectionFieldGroup>
                        <UserSelectionCount>
                            {isFetchingUsers ? 0 : selectedCount} {selectedUserGroupLabel} Selected
                        </UserSelectionCount>
                        <UserSelectionToggleBox>
                            All {selectedUserGroupLabel}
                            <UserSelectionSwitch
                                onChange={handleSelectAll}
                                checked={filterType === USER_GROUP.EMPLOYEES ? allEmployeesInPerk : allClientsInPerk}
                                disabled={isFetchingUsers}
                            />
                        </UserSelectionToggleBox>
                    </UserSelectionFieldGroup>
                </UserSelectionControls>

                {isFetchingUsers ? (
                    <StandardModalState minHeight="20rem">Loading users...</StandardModalState>
                ) : (
                    <StandardModalTableSection>
                        <StandardModalTableScroll maxHeight="min(30rem, calc(100vh - 27rem))">
                            <UserSelectionTable>
                                <StandardModalTable minWidth="760px">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "25%" }}>Name</th>
                                            <th style={{ width: "25%" }}>Department / Group</th>
                                            <th style={{ width: "25%" }}>Job Title</th>
                                            <th style={{ width: "25%" }}>Select User</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.length === 0 && (
                                            <tr>
                                                <td colSpan="4">
                                                    <StandardModalState minHeight="6rem" border="0">No users to display</StandardModalState>
                                                </td>
                                            </tr>
                                        )}
                                        {filteredUsers.map((user) => {
                                            const nameInitials = generateNameInitials(user.firstName, user.lastName);
                                            const selected = isUserSelected(user);
                                            return (
                                                <tr key={user.employeeUuid} className={selected ? "is-selected" : ""}>
                                                    <td>
                                                        <UserSelectionNameCell>
                                                            <UserSelectionAvatar>{nameInitials}</UserSelectionAvatar>
                                                            <UserSelectionCellText>{user.employeeName}</UserSelectionCellText>
                                                        </UserSelectionNameCell>
                                                    </td>
                                                    <td>{user.department || "-"}</td>
                                                    <td>{user.employmentPosition || "-"}</td>
                                                    <td>
                                                        {selected ? (
                                                            <UserSelectionCheckButton type="button" onClick={() => handleSelectUser(user)} aria-label="Deselect user">
                                                                <img src={BlueCheck} alt="" />
                                                            </UserSelectionCheckButton>
                                                        ) : (
                                                            <UserSelectionSelectButton type="button" onClick={() => handleSelectUser(user)} aria-label="Select user" />
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </StandardModalTable>
                            </UserSelectionTable>
                        </StandardModalTableScroll>
                    </StandardModalTableSection>
                )}
            </StandardModalSection>
        </StandardModal>
    );
};

export default UsersModal;
